import type { FullParseSubmissionContext } from "@/api/docParsing.service";
import {
  ABOUT_FIELDS,
  ARBITRATOR_NOT_CAPTURED,
  ARRIVAL_FIELDS,
  arbitratorsGroup,
  BARRISTER_FIELDS,
  CLIENT_FIELDS,
  GROUP_TITLES,
  groupOf,
  HEAD_OF_TEAM_FIELDS,
  hasTemplateSection,
  INTERVIEW_CONTACT_FIELDS,
  itemFrom,
  LEADING_FIELDS,
  LEGACY_BARRISTER_NOT_CAPTURED,
  LEGACY_MATTER_NOT_CAPTURED,
  MATTER_FIELDS,
  MAX_MATTERS_PER_KIND,
  MAX_SUMMARIES,
  metadataFields,
  NEXT_GENERATION_FIELDS,
  notCapturedField,
  orderSections,
  RISING_STAR_FIELDS,
  fieldFrom,
  SUMMARY_FIELDS,
  sectionOf,
  templateSectionOf,
} from "./originalSubmissionFields";

export type SubmissionSectionId =
  | "about"
  | "clients"
  | "team"
  | "matters"
  | "template"
  | "metadata";

export type SubmissionFieldValue =
  | { kind: "value"; value: string }
  | { kind: "notProvided" }
  | { kind: "notCaptured" };

export type SubmissionField = { label: string; fullLabel: string } & SubmissionFieldValue;

export type SubmissionItem = { fields: SubmissionField[] };

export type SubmissionGroup = { title: string; items: SubmissionItem[] };

export type SubmissionSection = {
  id: SubmissionSectionId;
  title: string;
  fields: SubmissionField[];
  groups: SubmissionGroup[];
  notCapturedItemFields: string[];
};

export type LegacyParsedJson = {
  head: Record<string, string | undefined>;
  clients: Record<string, string>[];
  contacts: Record<string, string>[];
  matters: Record<string, string>[];
  barristers?: Record<string, unknown>[];
};

export type SectionInput<T> = {
  data: T;
  submissionContext: FullParseSubmissionContext | null;
  parsedAt: string | null;
  templateType: string | null;
};

type LegacyRow = Record<string, string>;

const HEADING_KEYS = {
  headsOfTeam: "l500_heads_of_team",
  leadingPartners: "l500_leading_partners",
  risingPartners: "l500_rising_partners",
  hiresDepartures: "l500_hires_departures",
  interviewContacts: "l500_interview_contacts",
  publishableClients: "l500_publishable_clients",
  nonPublishableClients: "l500_nonpublishable_clients",
  summaryPrefix: "l500_top3",
};

const DOC_POSITION_PREFIXES = {
  leading: "partner: leading",
  nextGeneration: "partner: next generation",
};

const MATTER_SUMMARY_POSITION = "Matter summaries";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringEntries(record: Record<string, unknown>): Record<string, string> {
  const entries = Object.entries(record).filter(
    (entry): entry is [string, string] => typeof entry[1] === "string",
  );
  return Object.fromEntries(entries);
}

function narrowRows(value: unknown): LegacyRow[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter(isRecord).map(stringEntries);
}

/** Reads the stored parser output as the legacy shape, or null when it is not that shape. Never throws. */
export function narrowLegacyParsedJson(value: unknown): LegacyParsedJson | null {
  if (!isRecord(value) || !isRecord(value.head)) return null;
  const clients = narrowRows(value.clients);
  const contacts = narrowRows(value.contacts);
  const matters = narrowRows(value.matters);
  if (!clients || !contacts || !matters) return null;
  const barristers = Array.isArray(value.barristers)
    ? value.barristers.filter(isRecord)
    : undefined;
  return { head: stringEntries(value.head), clients, contacts, matters, barristers };
}

function headingKeyOf(row: LegacyRow): string {
  return row.Heading_Key ?? "";
}

function docPositionOf(row: LegacyRow): string {
  return (row.Doc_Position ?? "").toLowerCase();
}

function withHeadingKey(rows: LegacyRow[], headingKey: string): LegacyRow[] {
  return rows.filter(row => headingKeyOf(row) === headingKey);
}

function withDocPositionPrefix(rows: LegacyRow[], prefix: string): LegacyRow[] {
  return rows.filter(row => docPositionOf(row).startsWith(prefix));
}

function leadingIndividualRows(contacts: LegacyRow[]): LegacyRow[] {
  return contacts.filter(
    row =>
      headingKeyOf(row) === HEADING_KEYS.leadingPartners ||
      docPositionOf(row).startsWith(DOC_POSITION_PREFIXES.leading),
  );
}

function aboutSection(data: LegacyParsedJson): SubmissionSection {
  const { head } = data;
  const fields = [
    notCapturedField(ABOUT_FIELDS.lawyerName),
    fieldFrom(ABOUT_FIELDS.firmName, head.firm),
    fieldFrom(ABOUT_FIELDS.department, head.department_name),
    fieldFrom(ABOUT_FIELDS.partners, head.number_of_partners),
    fieldFrom(ABOUT_FIELDS.nonPartners, head.number_of_non_partners),
    notCapturedField(ABOUT_FIELDS.members),
    notCapturedField(ABOUT_FIELDS.practiceDifferentiator),
    notCapturedField(ABOUT_FIELDS.initiatives),
    notCapturedField(ABOUT_FIELDS.seniorClerk),
    notCapturedField(ABOUT_FIELDS.leadClerk),
    notCapturedField(ABOUT_FIELDS.otherClerks),
    notCapturedField(ABOUT_FIELDS.clerkingPractice),
  ];
  const heads = withHeadingKey(data.contacts, HEADING_KEYS.headsOfTeam).map(row =>
    itemFrom([
      [HEAD_OF_TEAM_FIELDS.name, row.name],
      [HEAD_OF_TEAM_FIELDS.location, row.location],
    ]),
  );
  return sectionOf("about", fields, [groupOf(GROUP_TITLES.headsOfTeam, heads)]);
}

function clientItems(rows: LegacyRow[]) {
  return rows.map(row =>
    itemFrom([
      [CLIENT_FIELDS.client, row.active_key_clients],
      [CLIENT_FIELDS.newClient, row.new_client],
    ]),
  );
}

function clientsSection(data: LegacyParsedJson): SubmissionSection {
  const publishable = withHeadingKey(data.clients, HEADING_KEYS.publishableClients);
  const active = withHeadingKey(data.clients, HEADING_KEYS.nonPublishableClients);
  return sectionOf(
    "clients",
    [notCapturedField(CLIENT_FIELDS.referees)],
    [
      groupOf(GROUP_TITLES.publishableClients, clientItems(publishable)),
      groupOf(GROUP_TITLES.activeKeyClients, clientItems(active)),
    ],
  );
}

function teamSection(data: LegacyParsedJson): SubmissionSection {
  const rising = withHeadingKey(data.contacts, HEADING_KEYS.risingPartners);
  const leading = leadingIndividualRows(data.contacts).map(row =>
    itemFrom([
      [LEADING_FIELDS.name, row.name],
      [LEADING_FIELDS.location, row.location],
      [LEADING_FIELDS.previouslyRanked, row.previously_ranked],
      [LEADING_FIELDS.supportingInformation, row.supporting_information],
    ]),
  );
  const nextGeneration = withDocPositionPrefix(rising, DOC_POSITION_PREFIXES.nextGeneration).map(
    row =>
      itemFrom([
        [NEXT_GENERATION_FIELDS.name, row.name],
        [NEXT_GENERATION_FIELDS.location, row.location],
        [NEXT_GENERATION_FIELDS.supportingInformation, row.supporting_information],
      ]),
  );
  const risingStars = rising
    .filter(
      row =>
        !docPositionOf(row).startsWith(DOC_POSITION_PREFIXES.nextGeneration) &&
        !docPositionOf(row).startsWith(DOC_POSITION_PREFIXES.leading),
    )
    .map(row =>
      itemFrom([
        [RISING_STAR_FIELDS.name, row.name],
        [RISING_STAR_FIELDS.location, row.location],
        [RISING_STAR_FIELDS.supportingInformation, row.supporting_information],
      ]),
    );
  const arrivals = withHeadingKey(data.contacts, HEADING_KEYS.hiresDepartures).map(row =>
    itemFrom([
      [ARRIVAL_FIELDS.name, row.name],
      [ARRIVAL_FIELDS.role, row.title],
      [ARRIVAL_FIELDS.direction, row.move_direction],
      [ARRIVAL_FIELDS.firm, row.move_firm],
      [ARRIVAL_FIELDS.date, row.move_date],
    ]),
  );
  return sectionOf(
    "team",
    [],
    [
      groupOf(GROUP_TITLES.leadingIndividuals, leading),
      groupOf(GROUP_TITLES.nextGeneration, nextGeneration),
      groupOf(GROUP_TITLES.risingStars, risingStars),
      groupOf(GROUP_TITLES.arrivalsAndDepartures, arrivals),
    ],
  );
}

function matterItems(rows: LegacyRow[]) {
  return rows.slice(0, MAX_MATTERS_PER_KIND).map(row =>
    itemFrom([
      [MATTER_FIELDS.client, row.Client],
      [MATTER_FIELDS.industry, row.Industry_Sector],
      [MATTER_FIELDS.description, row.Matter_Description],
      [MATTER_FIELDS.dealValue, row.Deal_Value],
      [MATTER_FIELDS.leadPartners, row.lead_partners],
      [MATTER_FIELDS.teamMembers, row.other_key_team_members],
    ]),
  );
}

function normalizedMatterPosition(row: LegacyRow): string {
  const lowered = docPositionOf(row);
  const pipeIndex = lowered.indexOf("|");
  const withoutSuffix = pipeIndex === -1 ? lowered : lowered.slice(0, pipeIndex);
  return withoutSuffix.replace(/-/g, " ").trim();
}

function isSummaryRow(row: LegacyRow): boolean {
  return (
    row.Doc_Position === MATTER_SUMMARY_POSITION ||
    headingKeyOf(row).startsWith(HEADING_KEYS.summaryPrefix)
  );
}

function isPublishableMatter(row: LegacyRow): boolean {
  const position = normalizedMatterPosition(row);
  if (position.includes("non publishable") || position.includes("confidential")) return false;
  return position.includes("publishable matter");
}

function mattersSection(data: LegacyParsedJson): SubmissionSection {
  const summaries = data.matters
    .filter(isSummaryRow)
    .slice(0, MAX_SUMMARIES)
    .map(row => itemFrom([[SUMMARY_FIELDS.summary, row.Matter_Description]]));
  const detailed = data.matters.filter(row => !isSummaryRow(row));
  const publishable = detailed.filter(isPublishableMatter);
  const nonPublishable = detailed.filter(row => !isPublishableMatter(row));
  return sectionOf(
    "matters",
    [],
    [
      groupOf(GROUP_TITLES.summaries, summaries),
      groupOf(GROUP_TITLES.publishableMatters, matterItems(publishable)),
      groupOf(GROUP_TITLES.nonPublishableMatters, matterItems(nonPublishable)),
    ],
    LEGACY_MATTER_NOT_CAPTURED,
  );
}

function barristerSection(data: LegacyParsedJson): SubmissionSection {
  const items = (data.barristers ?? []).map(row =>
    itemFrom([
      [BARRISTER_FIELDS.name, row.name],
      [BARRISTER_FIELDS.callYear, row.call_year],
      [BARRISTER_FIELDS.silkYear, row.silk_year],
    ]),
  );
  return templateSectionOf(groupOf(GROUP_TITLES.barristers, items), LEGACY_BARRISTER_NOT_CAPTURED);
}

function templateSection(data: LegacyParsedJson, templateType: string | null) {
  if (!hasTemplateSection(templateType)) return undefined;
  if (templateType === "bar") return barristerSection(data);
  return templateSectionOf(arbitratorsGroup(), ARBITRATOR_NOT_CAPTURED);
}

function metadataSection(
  data: LegacyParsedJson,
  input: SectionInput<LegacyParsedJson>,
): SubmissionSection {
  const contacts = withHeadingKey(data.contacts, HEADING_KEYS.interviewContacts).map(row =>
    itemFrom([
      [INTERVIEW_CONTACT_FIELDS.name, row.name],
      [INTERVIEW_CONTACT_FIELDS.jobTitle, row.title],
      [INTERVIEW_CONTACT_FIELDS.email, row.email],
      [INTERVIEW_CONTACT_FIELDS.phone, row.phone],
    ]),
  );
  return sectionOf("metadata", metadataFields(input.submissionContext, input.parsedAt), [
    groupOf(GROUP_TITLES.interviewContacts, contacts),
  ]);
}

/** Lays a legacy parse out as the six Original Submission sections. Values are plain text. */
export function buildLegacySections(input: SectionInput<LegacyParsedJson>): SubmissionSection[] {
  const { data, templateType } = input;
  return orderSections({
    about: aboutSection(data),
    clients: clientsSection(data),
    team: teamSection(data),
    matters: mattersSection(data),
    template: templateSection(data, templateType),
    metadata: metadataSection(data, input),
  });
}
